import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import type { CatalogFlowProgressAccent } from './CatalogFlowPositionProgressBar';

const fillClass: Record<CatalogFlowProgressAccent, string> = {
  blue: 'bg-blue-500 dark:bg-blue-400',
  indigo: 'bg-indigo-500 dark:bg-indigo-400',
  rose: 'bg-rose-500 dark:bg-rose-400',
  amber: 'bg-amber-500 dark:bg-amber-400',
  emerald: 'bg-emerald-500 dark:bg-emerald-400'
};

export interface CatalogFlowHeaderProgressBarProps {
  done: number;
  total: number;
  accent?: CatalogFlowProgressAccent;
  hasHeroImage?: boolean;
  className?: string;
  variant?: 'quiz' | 'exercise';
}

/** Completion progress for catalog-flow headers — sits below the subtitle/description. */
export const CatalogFlowHeaderProgressBar: React.FC<CatalogFlowHeaderProgressBarProps> = ({
  done,
  total,
  accent = 'emerald',
  hasHeroImage = false,
  className = '',
  variant = 'exercise'
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();

  if (total <= 0) return null;

  const safeDone = Math.min(Math.max(0, done), total);
  const percent = Math.round((safeDone / total) * 100);
  const progressKey = variant === 'quiz' ? 'catalogFlow.progress.passed' : 'catalogFlow.progress.reviewed';
  const trackClass = hasHeroImage
    ? 'bg-black/30 shadow-inner ring-1 ring-white/10'
    : 'bg-violet-200/80 shadow-inner dark:bg-gray-700/90';
  const labelClass = hasHeroImage
    ? 'text-white/85 tabular-nums'
    : 'text-slate-600 dark:text-slate-300 tabular-nums';

  return (
    <div
      className={`mt-3 w-full ${className}`.trim()}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={safeDone}
      aria-label={t(progressKey, { done: safeDone, total, percent })}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px] font-semibold sm:text-xs">
        <span className={labelClass}>
          {safeDone}/{total}
        </span>
        <span className={labelClass}>{percent}%</span>
      </div>
      <div
        className={`h-2.5 w-full overflow-hidden rounded-full ${trackClass}`}
        dir={direction}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none ${fillClass[accent]}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};
